import type { settingTypes } from '@ipfabric/utils';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

type AuthenticationType = 'login' | 'apiKey';

export type SilverPeakFormValues =
  BaseVendorFormValues<settingTypes.SilverPeakApiSettings> &
    settingTypes.SilverPeakUsernamePasswordAuthentication &
    settingTypes.ApiKeyAuthentication & {
      authentication?: AuthenticationType;
    };

type Props = VendorDialogProps<
  SilverPeakFormValues,
  settingTypes.SilverPeakApiSettings,
  settingTypes.UpdateSilverPeakApiSettings
>;

const resetSecretAttributes = (
  values: SilverPeakFormValues,
): SilverPeakFormValues => {
  return {
    ...values,
    username: '',
    password: '',
    loginType: undefined,
    apiKey: '',
  };
};

const loginTypeOptions = [
  { label: 'Local', value: 'Local' },
  { label: 'RADIUS', value: 'RADIUS' },
  { label: 'TACACS+', value: 'TACACS+' },
];

const BaseSilverPeakForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const { loginType } = msg.discoverySettings.vendorsApi.silverpeak;
  const formik = useFormik<SilverPeakFormValues>({
    initialValues: resetSecretAttributes({
      ...props.initialValues,
      authentication: 'login',
    }),
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable
    validate: (values) => {
      const { required, baseUrlStartsWith } =
        msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<SilverPeakFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }

      if (values.authentication === 'login') {
        if (!editMode && !values.username) {
          errors.username = required;
        }
        if (!editMode && !values.password) {
          errors.password = required;
        }
      }
      if (values.authentication === 'apiKey') {
        if (!editMode && !values.apiKey) {
          errors.apiKey = required;
        }
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit(
      ({ authentication, ...values }: SilverPeakFormValues) => {
        const { username, password, apiKey, ...formattedValues } = {
          ...values,
          ...getAdvancedSettingsDefaultValues(values),
        };

        if (isVendorUpdate(values) && props.dialogType === 'edit') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              username: username || formik.initialValues.username || undefined,
              password: password || formik.initialValues.password || undefined,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              apiKey: apiKey || formik.initialValues.apiKey || undefined,
            });
          }
        } else if (props.dialogType === 'add') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              username,
              password,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              apiKey,
            });
          }
        }
      },
    ),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<SilverPeakFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikSwitchField
        name='authentication'
        label='* Authentication'
        formik={formik}
        options={[
          {
            label: 'Username / Password',
            value: 'login',
          },
          {
            label: 'API Key',
            value: 'apiKey',
          },
        ]}
        size='small'
      />
      {formik.values.authentication === 'login' && (
        <div>
          <FormikVendorApiUsernamePasswordFields
            formik={formik}
            isEditMode={editMode}
          />
          <FormikSelectField
            formik={formik}
            label={loginType}
            name='loginType'
            nonClearable
            options={loginTypeOptions}
          />
        </div>
      )}
      {formik.values.authentication === 'apiKey' && (
        <FormikTextField
          formik={formik}
          name='apiKey'
          label='* API Key'
          placeholder={editMode ? '(unchanged)' : undefined}
          type='password'
        />
      )}
      <FormikVendorApiBaseUrlField formik={formik} />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const SilverPeakForm = memo(BaseSilverPeakForm);
